/**
 * useSubscription — read the current user's subscription state.
 *
 * Wraps `billingAgent.getSubscription()` in the project's standard
 * loading / error / data + refetch shape (see `useAdminSubscriptions`).
 * Reads from the backend's local subscription mirror (no Stripe round-trip),
 * so it is cheap enough to call from multiple consumers independently.
 *
 * The returned `subscription` mirrors the GET /api/billing/subscription
 * response: { plan_code, status, current_period_end, cancel_at_period_end,
 * trial_end }. Free users come back as { plan_code: 'explorer', status: null }.
 *
 * Polling (opt-in):
 *   Pass `{ pollMs }` to refetch on an interval while the consumer is mounted
 *   (e.g. the app-shell `DunningBanner` uses `useSubscription({ pollMs: 60000 })`
 *   so a webhook-driven status change — like `past_due` → `active` — is picked
 *   up within ~60s without a page reload). The interval is cleared on unmount.
 *   Omit `pollMs` (or pass `0`) to keep the original one-shot behavior — this is
 *   what PR2's `CheckoutReturnPage` relies on (it runs its own faster 1.5s poll).
 *
 * @param {object} [options]
 * @param {number} [options.pollMs=0] - Refetch interval in ms. `0` disables polling.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { billingAgent } from '../agents/billingAgent';

export function useSubscription({ pollMs = 0 } = {}) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingAgent.getSubscription();
      if (mountedRef.current) setSubscription(data);
      return data;
    } catch (err) {
      // Billing-API noise should not be loud for background polls; consumers
      // (e.g. DunningBanner) fail silent and simply keep the prior state.
      if (mountedRef.current) {
        setError(err?.message || 'Failed to load your subscription');
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Initial fetch on mount.
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Optional polling: refetch every `pollMs` while mounted. The initial fetch
  // above already ran, so the first interval tick is `pollMs` after mount.
  useEffect(() => {
    if (!pollMs || pollMs <= 0) return undefined;
    const id = setInterval(() => {
      refetch();
    }, pollMs);
    return () => clearInterval(id);
  }, [pollMs, refetch]);

  return { subscription, loading, error, refetch };
}

export default useSubscription;
